import { AppState } from 'src/store';
import { createSelector, OutputSelector } from 'reselect';
import { Lens } from 'monocle-ts';
import { ReduxSlice as FilterSlice } from 'src/components/FilterPanel/FilterPanel.container';
import { AssortmentScopeSelectorSlice } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { RightContainerSlice } from '../RightContainer/RightContainer.slice';
import type { PrintSize } from 'configuration-schemas/general';
import { PlanTrackerSlice } from 'src/pages/AssortmentBuild/Planning.types';
import type { CartButton, ContextFilterConfig, QuickReconButton, AgentButton } from 'configuration-schemas/confdefn';
import {
  getActiveContextPlanType,
  getActiveContext,
  getActiveContextString,
  getActivePerspectiveCartButtonConfig,
  getActivePerspectiveQuickReconButtonConfig,
  getActivePerspectiveAgentButtonConfig,
  hasContextsWithPlanTypeAllocation,
} from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import { HINDSIGHTING_CONTEXT } from 'src/utils/Domain/Constants';
import { getAssortmentCartLink } from 'src/pages/AssortmentBuild/AssortmentAdd/AssortmentAddBySearch/AssortmentAddBySearch.selectors';
import { getFiltersEnabled } from 'src/pages/NavigationShell/NavigationShell.selectors';

interface ScopeKeys {
  scopeStartKey: string;
  scopeEndKey: string;
  scopeRangesKey: string;
}

export interface StateSelection extends AssortmentScopeSelectorSlice {
  filters: FilterSlice;
  cartCount: number;
  rightContainer: RightContainerSlice;
  isViewPrintable: boolean;
  cartButtonConfig: CartButton | undefined;
  quickReconButtonConfig: QuickReconButton | undefined;
  agentButtonConfig: AgentButton | undefined;
  printSizes: PrintSize[];
  planTracker: PlanTrackerSlice;
  planType: 'assortment' | 'allocation';
  showAllocation: boolean;
  assortmentCartLink: string | undefined;
  showQuickReconcile?: boolean;
  filtersEnabled: boolean;
  contextFilterConfig?: ContextFilterConfig;
}

export interface StateProjection extends StateSelection, ScopeKeys {}

interface ScopeKeysFromContextStringSelector
  extends OutputSelector<AppState, ScopeKeys, (activeContextString: string) => ScopeKeys> {}

export const getScopeKeysFromContextString: ScopeKeysFromContextStringSelector = createSelector(
  [getActiveContextString],
  (activeContextString) => {
    let start = 'historyStart';
    let end = 'historyEnd';
    let scopeRangesKey = 'pastRangeList';

    if (activeContextString !== HINDSIGHTING_CONTEXT) {
      start = 'start';
      end = 'end';
      scopeRangesKey = 'rangeList';
    }

    return {
      scopeStartKey: start,
      scopeEndKey: end,
      scopeRangesKey,
    };
  },
);

function getCartCount(state: AppState) {
  return Lens.fromPath<AppState>()(['cart', 'cartItems']).get(state) || [];
}

function selectState(state: AppState): StateSelection {
  const { scope, print, filters, rightContainer } = state;
  const quickReconButtonConfig = getActivePerspectiveQuickReconButtonConfig(state);
  const cartButtonConfig = getActivePerspectiveCartButtonConfig(state);
  const agentButtonConfig = getActivePerspectiveAgentButtonConfig(state);
  const scopeKeys = getScopeKeysFromContextString(state);
  const assortmentCartLink = getAssortmentCartLink(state);
  const filtersEnabled = getFiltersEnabled(state);
  const planType = getActiveContextPlanType(state);
  const showAllocation = hasContextsWithPlanTypeAllocation(state);
  const activeContext = getActiveContext(state);

  return {
    ...scope,
    isViewPrintable: print.isViewPrintable,
    cartCount: getCartCount(state).length,
    filters,
    quickReconButtonConfig,
    rightContainer,
    cartButtonConfig,
    agentButtonConfig,
    printSizes: state.appConfig.tenantConfig.printSizes,
    planTracker: state.planTracker,
    planType,
    showAllocation,
    assortmentCartLink,
    showQuickReconcile: state.quickReconcile.showQuickReconcile,
    filtersEnabled,
    contextFilterConfig: activeContext?.filters,
    ...scopeKeys,
  };
}

export function projectState(stateSelection: StateSelection) {
  return {
    ...stateSelection,
  };
}

export default createSelector(selectState, projectState);
